/**
 * API de consulta para la torre de control (JWT del TMS en Authorization: Bearer).
 */
import type { Coordenadas, FechaDia, FechaIso, LineStringGeoJson, Pagina } from './comunes';
import type { Alerta } from './alertas';
import type { EstadoParada, EstadoViaje, TipoParada } from './viaje';
import type { MaraEventoEjecucionBody, MotivoParadaNoPlanificada, RolChofer, TipoEventoEjecucion } from './mara-entrante';

// ---------- Auth ----------

export const ROLES_USUARIO = ['DESPACHO', 'SUPERVISION', 'ADMIN'] as const;
export type RolUsuario = (typeof ROLES_USUARIO)[number];

/** POST /auth/login */
export interface LoginBody {
  email: string;
  contrasena: string;
}

export interface UsuarioSesion {
  id: number;
  email: string;
  nombre: string;
  rol: RolUsuario;
}

export interface LoginRespuesta {
  token: string;
  expiraEn: FechaIso;
  usuario: UsuarioSesion;
}

// ---------- Estado en vivo ----------

/**
 * Estado en vivo de un vehículo (Redis, clave tms:vivo:<vehiculoIdMicrotrack>).
 * Lo mantiene la ingesta con el evento más reciente de cada vehículo.
 */
export interface EstadoVivoVehiculo {
  vehiculoIdMicrotrack: string;
  vehiculoNombre: string | null;
  posicion: Coordenadas;
  velocidadKmh: number | null;
  rumbo: number | null;
  contacto: boolean | null;
  ralenti: boolean | null;
  llaveOperador: string | null;
  operadorId: string | null;
  operadorNombre: string | null;
  /** fechaHora del último evento procesado. */
  ultimoReporte: FechaIso;
  recibidoEn: FechaIso;
  /** Desde cuándo está detenido (velocidad <= VELOCIDAD_DETENIDO_KMH); null si se mueve. */
  detenidoDesde: FechaIso | null;
  /** Desde cuándo está en ralentí; null si no. */
  ralentiDesde: FechaIso | null;
  viajeId: number | null;
}

/** Estado de la unidad para la torre. Colores: EN_RUTA verde, DETENIDA ámbar, SIN_REPORTE rojo, SIN_ACTIVIDAD gris. */
export const ESTADOS_UNIDAD = ['EN_RUTA', 'DETENIDA', 'SIN_REPORTE', 'SIN_ACTIVIDAD'] as const;
export type EstadoUnidad = (typeof ESTADOS_UNIDAD)[number];

export interface ViajeEnUnidad {
  id: number;
  idMara: string;
  estado: EstadoViaje;
  empresa: string | null;
  unidadNegocio: string | null;
  cliente: string | null;
  /** Troncal de Mara (nombre) y sus textos de origen y destino. */
  troncalNombre: string;
  origenNombre: string | null;
  destinoNombre: string | null;
  /** Ruta asignada; null si la troncal no tiene rutas. */
  rutaNombre: string | null;
  /** Paradas pasadas: CUMPLIDA o EXCEDIDA. */
  paradasCumplidas: number;
  paradasTotal: number;
  /** 0..1, estimado por paradas pasadas. */
  avance: number;
  /** ETA a la próxima parada pendiente. */
  etaProximo: FechaIso | null;
  proximoNombre: string | null;
  choferPrincipal: string | null;
}

export interface UnidadFlota {
  vehiculoIdMicrotrack: string;
  patente: string | null;
  interno: string | null;
  /** Del viaje en curso o, si no hay, del último viaje de la unidad. */
  empresa: string | null;
  unidadNegocio: string | null;
  estado: EstadoUnidad;
  vivo: EstadoVivoVehiculo | null;
  /** Viaje EN_CURSO, o el próximo PLANIFICADO si no hay en curso. */
  viaje: ViajeEnUnidad | null;
  alertasAbiertas: number;
}

export interface IndicadoresFlota {
  enRuta: number;
  detenidas: number;
  sinReporte: number;
  sinActividad: number;
  viajesEnCurso: number;
  viajesPlanificadosHoy: number;
  alertasAbiertas: number;
  alertasCriticas: number;
}

/** GET /flota/estado?empresa=&unidadNegocio=&estado= */
export interface FlotaEstadoRespuesta {
  generadoEn: FechaIso;
  indicadores: IndicadoresFlota;
  unidades: UnidadFlota[];
}

// ---------- Viajes ----------

/**
 * GET /viajes?estado=&empresa=&unidadNegocio=&troncalId=&sinRuta=&desde=&hasta=&q=&pagina=&tamanio=
 * Tractor, semi y choferes: foto del maestro (patente = dominio de Mara), actualizada mientras el viaje no terminó.
 */
export interface ViajeResumen {
  id: number;
  idMara: string;
  estado: EstadoViaje;
  empresa: string | null;
  unidadNegocio: string | null;
  subunidadNegocio: string | null;
  cliente: string | null;
  numeroRecorrido: string | null;
  fechaHoraPlanificada: FechaIso;
  salidaReal: FechaIso | null;
  llegadaReal: FechaIso | null;
  troncal: { id: number; idMara: string; nombre: string; identificacion: string };
  /** Ruta asignada; null si la troncal no tiene rutas (VIAJE_SIN_RUTA). */
  ruta: { id: number; nombre: string } | null;
  tractor: { id: number; idMara: string; idMicrotrack: string | null; patente: string | null; interno: string | null };
  semi: { id: number; idMara: string; patente: string | null; interno: string | null } | null;
  choferes: { id: number; idMara: string; nombre: string; rol: RolChofer }[];
  /** Textos de origen y destino de la troncal de Mara. */
  origenNombre: string | null;
  destinoNombre: string | null;
  paradasCumplidas: number;
  paradasExcedidas: number;
  paradasOmitidas: number;
  paradasTotal: number;
  alertasAbiertas: number;
  kmGps: number | null;
}

export type ViajesRespuesta = Pagina<ViajeResumen>;

/** Parada del viaje: copia de una parada de la ruta asignada (parada_viaje) con su progreso. */
export interface ParadaDetalle {
  id: number;
  orden: number;
  /** Parada de la ruta de la que se copió; null si la ruta se editó y la borró. */
  paradaRutaId: number | null;
  nombre: string;
  tipo: TipoParada;
  ubicacion: Coordenadas;
  radioM: number;
  obligatoria: boolean;
  duracionMinEsperadaMin: number | null;
  duracionMaxEsperadaMin: number | null;
  etaActual: FechaIso | null;
  estado: EstadoParada;
  /** Primera detección por GPS. */
  detectadaEn: FechaIso | null;
  /** Entrada y salida del geofence por GPS. */
  llegadaReal: FechaIso | null;
  salidaReal: FechaIso | null;
  permanenciaMin: number | null;
  /** Registro de Mara (`parada.registrada`). */
  declaradaEn: FechaIso | null;
  cantidadEntregada: number | null;
  unidadMedida: string | null;
  observacion: string | null;
}

/** Parada fuera de la ruta que el chofer declaró por Mara. */
export interface ParadaNoPlanificadaDetalle {
  id: number;
  motivo: MotivoParadaNoPlanificada;
  observacion: string | null;
  fechaHora: FechaIso;
  ubicacion: Coordenadas | null;
  /** Detención de GPS que la confirma; null si no se vio. */
  detencionId: number | null;
}

/** Aviso de ejecución recibido de Mara (log inmutable evento_ejecucion). Pestaña «Avisos de Mara». */
export interface AvisoMaraDetalle {
  id: number;
  eventoId: string;
  tipo: TipoEventoEjecucion;
  fechaHora: FechaIso;
  recibidoEn: FechaIso;
  /** Cuerpo tal como llegó. */
  cuerpo: MaraEventoEjecucionBody;
}

export interface Detencion {
  id: number;
  vehiculoIdMicrotrack: string;
  viajeId: number | null;
  /** Parada de la ruta del viaje (parada_viaje) donde ocurrió; null si fue fuera de ellas. */
  paradaId: number | null;
  /** Parada no planificada declarada que esta detención confirma; null si no hay. */
  paradaNoPlanificadaId: number | null;
  /** true si ocurrió fuera de toda parada de la ruta y sin parada no planificada declarada. */
  fueraDePlan: boolean;
  ubicacion: Coordenadas;
  inicio: FechaIso;
  fin: FechaIso | null;
  duracionMin: number | null;
}

export const TIPOS_HITO = [
  'PLAN_RECIBIDO',
  'PLAN_MODIFICADO',
  'INICIO',
  'PARADA_DETECTADA',
  'PARADA_DECLARADA',
  'PARADA_NO_PLANIFICADA',
  'DETENCION',
  'ALERTA',
  'FIN',
  'CANCELADO',
] as const;
export type TipoHito = (typeof TIPOS_HITO)[number];

/** Hito de la línea de tiempo del viaje (pestaña Eventos del detalle). Se arma al consultar. */
export interface HitoViaje {
  fechaHora: FechaIso;
  tipo: TipoHito;
  descripcion: string;
  ubicacion: Coordenadas | null;
  /** Id de la entidad relacionada (parada, alerta, detención), si aplica. */
  refId: number | null;
}

/** Ruta asignada al viaje, con los datos del momento de consulta. */
export interface RutaViaje {
  id: number;
  nombre: string;
  km: number;
  duracionMin: number;
  porDefecto: boolean;
  activa: boolean;
}

/** GET /viajes/:id */
export interface ViajeDetalle extends ViajeResumen {
  ruta: RutaViaje | null;
  centroCosto: string | null;
  /** Odómetro informado por Mara. */
  kmInicial: number | null;
  kmFinal: number | null;
  /** Copia de la traza de la ruta al asignarla. */
  trazaPlanificada: LineStringGeoJson | null;
  paradas: ParadaDetalle[];
  paradasNoPlanificadas: ParadaNoPlanificadaDetalle[];
  detenciones: Detencion[];
  avisosMara: AvisoMaraDetalle[];
  alertas: Alerta[];
  hitos: HitoViaje[];
  vivo: EstadoVivoVehiculo | null;
  /** 0..1 */
  avance: number;
  recibidoEn: FechaIso;
}

export interface PuntoRecorrido {
  fechaHora: FechaIso;
  latitud: number;
  longitud: number;
  velocidadKmh: number | null;
  rumbo: number | null;
  contacto: boolean | null;
  tipoEvento: string;
}

/**
 * GET /viajes/:id/recorrido?muestreoSeg=
 * Con muestreoSeg se devuelve como máximo un punto por intervalo (el primero de cada ventana).
 */
export interface RecorridoRespuesta {
  viajeId: number;
  puntos: PuntoRecorrido[];
  /** El mismo recorrido como LineString, listo para el mapa. */
  linea: LineStringGeoJson | null;
  totalEventos: number;
}

// ---------- Jornadas y estado diario ----------

export const TIPOS_ACTIVIDAD = ['CONDUCCION', 'DETENIDO'] as const;
export type TipoActividad = (typeof TIPOS_ACTIVIDAD)[number];

export interface TramoActividad {
  desde: FechaIso;
  hasta: FechaIso;
  tipo: TipoActividad;
}

/** GET /jornadas?fecha=YYYY-MM-DD */
export interface JornadaChofer {
  llaveOperador: string;
  choferIdMara: string | null;
  nombre: string | null;
  fecha: FechaDia;
  inicio: FechaIso;
  fin: FechaIso;
  horasConduccion: number;
  km: number;
  actividad: TramoActividad[];
}

export interface JornadasRespuesta {
  fecha: FechaDia;
  jornadas: JornadaChofer[];
}

export const ESTADOS_DIARIOS_UNIDAD = ['EN_VIAJE', 'ACTIVA_SIN_VIAJE', 'SIN_ACTIVIDAD'] as const;
export type EstadoDiarioUnidad = (typeof ESTADOS_DIARIOS_UNIDAD)[number];

/** GET /unidades/estado-diario?fecha=YYYY-MM-DD */
export interface EstadoDiarioUnidadDto {
  vehiculoIdMicrotrack: string;
  patente: string | null;
  fecha: FechaDia;
  estado: EstadoDiarioUnidad;
  horasEnViaje: number;
  km: number;
}

/**
 * GET /unidades/estado-diario?fecha=YYYY-MM-DD            → un día
 * GET /unidades/estado-diario?desde=YYYY-MM-DD&hasta=...  → rango (máx. 31 días); `fecha` = hasta
 */
export interface EstadoDiarioRespuesta {
  fecha: FechaDia;
  desde: FechaDia;
  hasta: FechaDia;
  unidades: EstadoDiarioUnidadDto[];
}

/** GET /catalogos: valores distintos presentes en los viajes (para los filtros de la torre). */
export interface CatalogosRespuesta {
  empresas: string[];
  unidadesNegocio: string[];
  subunidadesNegocio: string[];
  clientes: string[];
}

// ---------- SSE ----------

/**
 * GET /stream (text/event-stream). Autenticación: ?ticket=<ticket> obtenido con POST /stream/ticket
 * (JWT en header; ticket de un solo uso, vida 60 s), para no dejar el JWT en logs de proxies.
 * Cada mensaje SSE tiene `event: <tipo>` y `data: <JSON>`. Heartbeat cada 25 s con `event: ping`.
 */
/** POST /stream/ticket */
export interface TicketStreamRespuesta {
  ticket: string;
  expiraEn: FechaIso;
}

export interface EventosStream {
  posicion: EstadoVivoVehiculo;
  viaje: { viajeId: number; idMara: string; estado: EstadoViaje; rutaId?: number | null; paradaId?: number; estadoParada?: EstadoParada };
  alerta: Alerta;
  ping: { ts: FechaIso };
}
export type TipoEventoStream = keyof EventosStream;
/** Unión discriminada por `tipo`: al chequear `tipo` se acota `datos`. */
export type MensajeStream = { [T in TipoEventoStream]: { tipo: T; datos: EventosStream[T] } }[TipoEventoStream];

// ---------- Health ----------

export interface HealthRespuesta {
  estado: 'ok' | 'degradado';
  db: { ok: boolean; latenciaMs: number | null; error?: string };
  redis: { ok: boolean; latenciaMs: number | null; error?: string };
  cola: { ok: boolean; esperando: number; activos: number; fallidos: number; error?: string };
  /** Motor de ruteo (OSRM, D-35). Lo agrega la etapa 3. */
  ruteo?: { ok: boolean; latenciaMs: number | null; error?: string };
}
